import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Link,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';

import { formatShutter, formatTagValue, formatTakenAt, ImageMetadata, mapLink } from '../uploadsApi';

const SETTING_LABELS: Record<string, string> = {
    FNumber: 'Aperture',
    ExposureTime: 'Shutter',
    ISOSpeedRatings: 'ISO',
    PhotographicSensitivity: 'ISO',
    FocalLength: 'Focal length',
    FocalLengthIn35mmFilm: 'Focal length (35 mm)',
    ExposureProgram: 'Exposure program',
    ExposureMode: 'Exposure mode',
    ExposureBiasValue: 'Exposure bias',
    MeteringMode: 'Metering',
    WhiteBalance: 'White balance',
    SceneCaptureType: 'Scene type',
    DigitalZoomRatio: 'Digital zoom',
    SubjectDistance: 'Subject distance',
    BrightnessValue: 'Brightness',
    flash_fired: 'Flash',
};

const settingValue = (key: string, value: unknown): string => {
    if (key === 'FNumber' && typeof value === 'number') return `f/${Math.round(value * 100) / 100}`;
    if (key === 'ExposureTime' && typeof value === 'number') return formatShutter(value);
    if ((key === 'FocalLength' || key === 'FocalLengthIn35mmFilm') && typeof value === 'number') {
        return `${Math.round(value * 10) / 10} mm`;
    }
    if (key === 'flash_fired') return value ? 'Fired' : 'Did not fire';
    return formatTagValue(value);
};

const Facts: React.FC<{ rows: Array<[string, React.ReactNode]>; label: string }> = ({ rows, label }) => (
    <Table size="small" aria-label={label} sx={{ '& td': { borderBottom: 0, py: 0.25, px: 0 } }}>
        <TableBody>
            {rows.map(([name, value]) => (
                <TableRow key={name}>
                    <TableCell sx={{ color: 'text.secondary', width: '38%', verticalAlign: 'top' }}>{name}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{value}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.8 }}>
        {children}
    </Typography>
);

export interface ExifPanelProps {
    /** null when nothing was read: not an image, or uploaded before EXIF was kept. */
    metadata: ImageMetadata | null;
    isImage: boolean;
}

/** Everything a picture said about itself: the summary people ask about, then every tag, then the raw JSON. */
export const ExifPanel: React.FC<ExifPanelProps> = ({ metadata, isImage }) => {
    if (!isImage) {
        return <Typography color="text.secondary">Only images carry EXIF.</Typography>;
    }
    if (!metadata) {
        return (
            <Typography color="text.secondary">
                No metadata was kept for this image. It was uploaded before EXIF was read; “Read metadata again” reads it
                from the stored file.
            </Typography>
        );
    }

    const tagCount = Object.keys(metadata.tags).length;
    const camera = Object.entries(metadata.camera);
    const settings = Object.entries(metadata.settings);
    const gps = metadata.gps;
    const hasPosition = gps?.latitude !== undefined && gps?.longitude !== undefined;
    const hasNothing = !metadata.taken_at && camera.length === 0 && settings.length === 0 && !gps && tagCount === 0;
    const raw = JSON.stringify(metadata, null, 2);

    return (
        <Box>
            {hasNothing && (
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                    This image carries no EXIF: no date, camera or location. Messaging apps and screenshots strip it.
                </Typography>
            )}

            <Facts
                label="Image"
                rows={[
                    ['Format', metadata.image.format ?? '—'],
                    [
                        'Size',
                        metadata.image.width && metadata.image.height
                            ? `${metadata.image.width} × ${metadata.image.height} px`
                            : '—',
                    ],
                    ...(metadata.image.orientation ? ([['Orientation', metadata.image.orientation]] as Array<[string, string]>) : []),
                    ...(metadata.taken_at ? ([['Taken', formatTakenAt(metadata.taken_at)]] as Array<[string, string]>) : []),
                ]}
            />

            {camera.length > 0 && (
                <>
                    <Heading>Camera</Heading>
                    <Facts label="Camera" rows={camera.map(([k, v]) => [k, formatTagValue(v)])} />
                </>
            )}

            {settings.length > 0 && (
                <>
                    <Heading>Settings</Heading>
                    <Facts label="Settings" rows={settings.map(([k, v]) => [SETTING_LABELS[k] ?? k, settingValue(k, v)])} />
                </>
            )}

            {gps && (
                <>
                    <Heading>Location</Heading>
                    <Facts
                        label="Location"
                        rows={[
                            ...(hasPosition
                                ? ([
                                      ['Latitude', String(gps.latitude)],
                                      ['Longitude', String(gps.longitude)],
                                  ] as Array<[string, string]>)
                                : ([['Position', 'GPS wrote no usable coordinates']] as Array<[string, string]>)),
                            ...(gps.altitude_m !== undefined ? ([['Altitude', `${gps.altitude_m} m`]] as Array<[string, string]>) : []),
                            ...(gps.direction_deg !== undefined ? ([['Facing', `${gps.direction_deg}°`]] as Array<[string, string]>) : []),
                            ...(gps.speed_kmh !== undefined ? ([['Speed', `${gps.speed_kmh} km/h`]] as Array<[string, string]>) : []),
                            ...(gps.timestamp ? ([['GPS time (UTC)', gps.timestamp]] as Array<[string, string]>) : []),
                        ]}
                    />
                    {hasPosition && (
                        <Box sx={{ mt: 0.5 }}>
                            <Link
                                href={mapLink(gps.latitude as number, gps.longitude as number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <PlaceIcon fontSize="small" /> Open in OpenStreetMap
                            </Link>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Opening the map sends these coordinates to OpenStreetMap. Nothing here loads it for you.
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {tagCount > 0 && (
                <Accordion disableGutters variant="outlined" sx={{ mt: 1.5 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>All EXIF tags ({tagCount})</AccordionSummary>
                    <AccordionDetails>
                        <Facts label="All EXIF tags" rows={Object.entries(metadata.tags).map(([k, v]) => [k, formatTagValue(v)])} />
                    </AccordionDetails>
                </Accordion>
            )}

            <Accordion disableGutters variant="outlined" sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Raw JSON</AccordionSummary>
                <AccordionDetails>
                    <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => navigator.clipboard?.writeText(raw)}
                        sx={{ mb: 1 }}
                    >
                        Copy
                    </Button>
                    <Box component="pre" sx={{ m: 0, fontSize: 12, overflow: 'auto', maxHeight: 320 }}>
                        {raw}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
